// First-Run Coach — action-driven guided tour.
// Auto-fires for guests + regular users on first entry.
// Each step requires a real user action (smart completion).
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, X, CheckCircle2, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { client } from "@/lib/api";
import { COACH_EVENT, COACH_STEPS, GUEST_COACH_KEY } from "@/lib/coach";

const PADDING = 8; // px around the spotlight target

function readGuestState() {
  try {
    const raw = localStorage.getItem(GUEST_COACH_KEY);
    return raw ? JSON.parse(raw) : { coach_done: false, coach_step: 0, skipped: false };
  } catch {
    return { coach_done: false, coach_step: 0, skipped: false };
  }
}

function writeGuestState(s) {
  try { localStorage.setItem(GUEST_COACH_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

// Robust guest detection — closure-safe. Checks the session token prefix
// directly so it works even before AuthContext has hydrated.
function tokenLooksLikeGuest() {
  try {
    const t = localStorage.getItem("session_token") || "";
    return t.startsWith("guest_sess_");
  } catch {
    return false;
  }
}

export default function CoachTour() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isGuest = !!user?.is_guest;
  const onboarded = !!user?.preferences?.onboarded;

  const [stepIdx, setStepIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [forced, setForced] = useState(false); // user clicked "Replay"
  const [modalOpen, setModalOpen] = useState(false); // hide overlay when an action modal is open
  const [celebrate, setCelebrate] = useState(null); // {step,label} — brief "✓ Step N done" banner
  const persistedRef = useRef(false);

  // ----- detect open action modals (IdeaModal / ScriptModal / GoalModal) -----
  // When the user is mid-action inside a modal, hide the coach overlay so the
  // form is fully visible and untinted. Listeners stay alive, so markCoachStep
  // still advances the tour on save.
  useEffect(() => {
    if (!open) { setModalOpen(false); return; }
    const MODAL_SELECTORS = '[data-testid="idea-modal"], [data-testid="script-modal"], [data-testid="goal-modal"]';
    const check = () => setModalOpen(!!document.querySelector(MODAL_SELECTORS));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [open, stepIdx]);

  // ----- decide whether to show on mount -----
  useEffect(() => {
    if (!user) return;
    let initialStep = 0;
    let alreadyDone = false;
    let skipped = false;
    if (isGuest) {
      const s = readGuestState();
      initialStep = Math.max(0, Math.min(COACH_STEPS.length - 1, s.coach_step || 0));
      alreadyDone = !!s.coach_done;
      skipped = !!s.skipped;
    } else {
      const prefs = user.preferences || {};
      initialStep = Math.max(0, Math.min(COACH_STEPS.length - 1, prefs.coach_step || 0));
      alreadyDone = !!prefs.coach_done;
      // regular users only see coach after they've finished onboarding
      if (!onboarded && !forced) return;
    }
    if (forced) {
      setStepIdx(0);
      setOpen(true);
      return;
    }
    if (alreadyDone || skipped) return;
    setStepIdx(initialStep);
    setOpen(true);
  }, [user?.user_id, onboarded, isGuest, forced]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = COACH_STEPS[stepIdx];

  const persistProgress = useCallback(async (next) => {
    const guestMode = isGuest || tokenLooksLikeGuest();
    if (guestMode) {
      const cur = readGuestState();
      writeGuestState({ ...cur, ...next });
    } else {
      try {
        await client.patch("/auth/preferences", next);
        refresh?.();
      } catch { /* noop */ }
    }
  }, [isGuest, refresh]);

  // ----- navigate to step's route when step changes -----
  useEffect(() => {
    if (!open || !current?.route) return;
    if (location.pathname !== current.route) {
      navigate(current.route);
    }
  }, [open, stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- find target rect (with retry while DOM mounts) -----
  useEffect(() => {
    if (!open) { setRect(null); return; }
    let raf;
    let tries = 0;
    let scrolled = false;
    const find = () => {
      const el = document.querySelector(current?.targetSelector || "");
      if (el) {
        if (!scrolled) {
          // Scroll target to upper third of viewport so tooltip (placed below
          // the target by default) always has room — prevents the card from
          // being cut off at the bottom of the screen.
          try {
            const r = el.getBoundingClientRect();
            const vh = window.innerHeight;
            const desiredTop = vh * 0.20;
            const delta = r.top - desiredTop;
            if (Math.abs(delta) > 24) {
              window.scrollBy({ top: delta, behavior: "smooth" });
            } else {
              el.scrollIntoView({ block: "start", behavior: "smooth" });
            }
          } catch { /* noop */ }
          scrolled = true;
        }
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tries < 60) {
        tries += 1;
        raf = requestAnimationFrame(find);
      } else {
        setRect(null);
      }
    };
    find();
    const onResize = () => find();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, stepIdx, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- listen for step-done events from action points -----
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e?.detail?.step === current?.id) {
        advance();
      }
    };
    window.addEventListener(COACH_EVENT, handler);
    return () => window.removeEventListener(COACH_EVENT, handler);
  }, [open, stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- auto-complete certain steps on route arrival -----
  useEffect(() => {
    if (!open || !current) return;
    if (current.autoCompleteOnRoute && location.pathname === current.route) {
      const t = setTimeout(() => advance(), 700);
      return () => clearTimeout(t);
    }
  }, [open, stepIdx, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = useCallback(() => {
    if (persistedRef.current) return;
    setStepIdx((idx) => {
      const nextIdx = idx + 1;
      // brief celebration banner so the user gets visible feedback even from
      // inside a modal (modal overlay hides the main coach card).
      setCelebrate({ n: idx + 1, label: COACH_STEPS[idx]?.id });
      if (nextIdx >= COACH_STEPS.length) {
        persistedRef.current = true;
        setOpen(false);
        persistProgress({ coach_done: true, coach_step: COACH_STEPS.length });
        return idx;
      }
      persistProgress({ coach_step: nextIdx });
      return nextIdx;
    });
  }, [persistProgress]);

  // auto-hide celebration banner after 2s
  useEffect(() => {
    if (!celebrate) return;
    const t = setTimeout(() => setCelebrate(null), 2200);
    return () => clearTimeout(t);
  }, [celebrate]);

  const skip = useCallback(async () => {
    // Always write a best-effort localStorage flag first — guarantees
    // persistence even if auth context is stale or PATCH fails.
    const guestMode = isGuest || tokenLooksLikeGuest();
    if (guestMode) {
      writeGuestState({ ...readGuestState(), skipped: true, coach_done: true });
    }
    setOpen(false);
    if (!guestMode) {
      try { await client.patch("/auth/preferences", { coach_done: true }); refresh?.(); }
      catch { /* noop */ }
    }
  }, [isGuest, refresh]);

  // expose replay trigger via custom window event
  useEffect(() => {
    const replay = async () => {
      persistedRef.current = false;
      const guestMode = isGuest || tokenLooksLikeGuest();
      if (guestMode) {
        writeGuestState({ coach_done: false, coach_step: 0, skipped: false });
      } else {
        try { await client.patch("/auth/preferences", { coach_done: false, coach_step: 0 }); refresh?.(); }
        catch { /* noop */ }
      }
      setForced((v) => !v); // toggle to re-trigger mount-effect
      setStepIdx(0);
      setOpen(true);
    };
    window.addEventListener("pintaflow:coach-replay", replay);
    return () => window.removeEventListener("pintaflow:coach-replay", replay);
  }, [isGuest, refresh]);

  // Card position is fixed in viewport — no dynamic measurement needed.
  // Card sits in a stable fixed position (bottom on mobile, bottom-right on
  // desktop) — it does NOT move with scroll or chase the target. The spotlight
  // ring still tracks the target so users can see what to interact with, but
  // the instruction card itself stays put so it never floats around or gets
  // cut off as the user scrolls.
  const tooltipStyle = useMemo(() => {
    const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768;
    if (isDesktop) {
      return { right: 24, bottom: 24, left: "auto", top: "auto" };
    }
    return { left: 16, right: 16, bottom: 16, top: "auto", width: "auto" };
  }, []);

  if (!open || !current) return null;
  // Hide overlay completely while user is inside an action modal (idea / script
  // / goal). Listeners stay mounted so the tour still advances on save.
  // Celebration banner still renders (it's z-index above modal) so the user
  // gets visible feedback even while reading a modal.
  if (modalOpen) {
    if (!celebrate) return null;
    return (
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[120] pointer-events-none px-4 py-2 rounded-full text-sm flex items-center gap-2 animate-fadeIn"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--seal)",
          color: "var(--ink)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.45), 0 0 0 6px rgba(216,168,87,0.10)",
        }}
        data-testid="coach-celebrate"
      >
        <CheckCircle2 size={14} style={{ color: "var(--seal)" }} />
        <span>Step {celebrate.n} complete — moving forward</span>
      </div>
    );
  }

  const stepNum = stepIdx + 1;
  const total = COACH_STEPS.length;

  return (
    <div className="fixed inset-0 z-[97] pointer-events-none" data-testid="coach-tour">
      {/* Floating "Step N complete" celebration — visible briefly on every advance */}
      {celebrate && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[120] px-4 py-2 rounded-full text-sm flex items-center gap-2"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--seal)",
            color: "var(--ink)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.45), 0 0 0 6px rgba(216,168,87,0.10)",
          }}
          data-testid="coach-celebrate"
        >
          <CheckCircle2 size={14} style={{ color: "var(--seal)" }} />
          <span>Step {celebrate.n} complete — moving forward</span>
        </div>
      )}
      {/* Dim overlay with a "hole" cut around target.
          pointer-events-none on the entire svg so real user clicks pass
          THROUGH the dim onto the highlighted target (smart completion). */}
      {rect ? (
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ width: "100%", height: "100%" }}
          preserveAspectRatio="none"
        >
          <defs>
            <mask id="coach-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={Math.max(0, rect.left - PADDING)}
                y={Math.max(0, rect.top - PADDING)}
                width={rect.width + PADDING * 2}
                height={rect.height + PADDING * 2}
                rx="14"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.62)" mask="url(#coach-mask)" />
          {/* brass glow ring */}
          <rect
            x={Math.max(0, rect.left - PADDING)}
            y={Math.max(0, rect.top - PADDING)}
            width={rect.width + PADDING * 2}
            height={rect.height + PADDING * 2}
            rx="14"
            fill="none"
            stroke="rgba(216,168,87,0.85)"
            strokeWidth="2"
            style={{ filter: "drop-shadow(0 0 12px rgba(216,168,87,0.55))" }}
          />
        </svg>
      ) : (
        <div className="absolute inset-0 pointer-events-auto" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(2px)" }} />
      )}

      {/* Tooltip card — fixed in viewport, doesn't chase target on scroll */}
      <div
        className="fixed pointer-events-auto md:w-[360px] max-w-[calc(100vw-32px)] rounded-2xl overflow-hidden"
        style={{
          ...tooltipStyle,
          background: "var(--surface)",
          border: "1px solid var(--ink-22)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(216,168,87,0.10)",
        }}
        data-testid="coach-card"
      >
        {/* Header: step counter + close */}
        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
          <div className="eyebrow flex items-center gap-2">
            <Sparkles size={12} style={{ color: "var(--seal)" }} />
            <span data-testid="coach-progress">{stepNum} / {total}</span>
          </div>
          <button onClick={skip} className="btn-ghost" data-testid="coach-skip" aria-label="Skip tour">
            <X size={14} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-5 mb-3">
          <div className="flex gap-1.5">
            {COACH_STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1 rounded-full flex-1 transition-all"
                style={{ background: i <= stepIdx ? "var(--seal)" : "var(--ink-22)" }}
              />
            ))}
          </div>
        </div>

        <div className="px-5 pb-3">
          <div className="eyebrow mb-2">{current.eyebrow}</div>
          <h2
            className="font-heading text-2xl leading-[1.1]"
            style={{ fontVariationSettings: "'opsz' 64, 'wght' 450" }}
          >
            {current.title}{" "}
            <span className="font-display-italic">{current.titleItalic}</span>
          </h2>
          <p className="text-sm mt-3 leading-relaxed" style={{ color: "var(--ink-72)" }}>
            {current.body}
          </p>
          {current.hint && (
            <div
              className="mt-4 px-3 py-2 rounded-lg flex items-center gap-2 text-xs"
              style={{
                background: "rgba(216,168,87,0.10)",
                border: "1px solid rgba(216,168,87,0.30)",
                color: "var(--ink)",
              }}
              data-testid="coach-hint"
            >
              <ArrowRight size={12} style={{ color: "var(--seal)" }} />
              {current.hint}
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-between gap-2 px-5 py-3"
          style={{ borderTop: "1px solid var(--ink-22)" }}
        >
          <button onClick={skip} className="btn-ghost text-xs" data-testid="coach-skip-text">
            Maybe later
          </button>
          <button
            onClick={advance}
            className="btn-secondary text-xs"
            data-testid="coach-skip-step"
            title="Skip this step"
          >
            Skip step <ArrowRight size={12} />
          </button>
        </div>

        {/* completed mini-indicator */}
        {stepIdx > 0 && (
          <div className="flex items-center gap-1.5 px-5 py-2 text-[10px]" style={{ background: "var(--bg)", borderTop: "1px solid var(--ink-22)", color: "var(--ink-55)" }}>
            <CheckCircle2 size={11} style={{ color: "var(--seal)" }} />
            {stepIdx} step{stepIdx > 1 ? "s" : ""} complete
          </div>
        )}
      </div>
    </div>
  );
}
